/**
 * Reads staff tenant access off Firebase custom claims.
 *
 * `/tenant/select` starts from these same claims and only narrows them: it
 * expands `*` and `enterprise`, then drops clinics that no longer exist or where
 * the person has no active staff account. So when the claims name exactly one
 * clinic and nothing that expands, the list can hold at most that clinic, and
 * the request can be skipped.
 */

/** Login never lands here, so it can't be taken from the claims alone. */
export const BILLING_TENANT = "billing";

type Claims = Record<string, unknown>;

function claimedTenants(claims: Claims): string[] {
  return Array.isArray(claims.tenants) ? (claims.tenants as string[]) : [];
}

function hasEnterpriseClaim(claims: Claims): boolean {
  const { enterprise } = claims;
  return Array.isArray(enterprise) ? enterprise.length > 0 : !!enterprise;
}

/**
 * The one clinic these claims can reach, or null when only `/tenant/select`
 * can say: several tenants, the `*` wildcard, or an enterprise claim.
 */
export function singleClaimedTenant(claims: Claims): string | null {
  const tenants = claimedTenants(claims);
  if (tenants.length !== 1 || tenants[0] === "*") return null;
  if (hasEnterpriseClaim(claims)) return null;
  return tenants[0];
}

/** Whether these claims could reach more than one clinic. */
export function mayHaveMultipleTenants(claims: Claims): boolean {
  const tenants = claimedTenants(claims);
  return (
    tenants.length > 1 || tenants.includes("*") || hasEnterpriseClaim(claims)
  );
}
